import { AdminApiError, type AdminClient } from "../api.js";
import { clear, h } from "../dom.js";
import type { CompanySlug, IntercompanyPair, IntercompanySnapshot, StaffRole } from "../types.js";
import { moneyText, pluralText } from "../ui/display-words.js";
import { decimalToCents, formatCents } from "../ui/money.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import { navLabel, sentence } from "../ui/sentences.js";
import {
  createCompanyDirectory,
  type CompanyDirectory,
  type CompanyDirectoryRow,
} from "../ui/face.js";
import * as face from "../ui/face.js";
import { readerRole } from "../ui/facts.js";

export interface IntercompanyPageOptions {
  client: AdminClient;
  root: HTMLElement;
  companyLabels?: ReadonlyMap<string, string>;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /**
   * The reader's role, as the shell holds it (owner decision 10: an unknown
   * role reads as the account manager, never the owner).
   */
  role?: StaffRole;
}

const PAGE_TITLE = navLabel("intercompany");

function showDetails(detail: string): HTMLElement {
  return h("details", { class: "disclosure", "data-role": "show-details" }, [
    h("summary", {}, ["Show details"]),
    h("p", {}, [detail]),
  ]);
}

/**
 * A company as this page names it, through the shell's one directory
 * (owner decision 14: a company we cannot name reads "this company", never its
 * slug). Nothing is known when the directory has no name for it, so the
 * sentence below never claims to name someone it cannot.
 */
function companyFace(
  slug: CompanySlug | null | undefined,
  dir: CompanyDirectory,
): { name: string; known: boolean } {
  const name = face.company(slug ?? "", dir, "sentence");
  return { name, known: slug !== null && slug !== undefined && dir.name(slug) !== null };
}

function labelFor(slug: CompanySlug | null | undefined, dir: CompanyDirectory): string {
  return companyFace(slug, dir).name;
}

function statusClass(status: IntercompanyPair["status"]): string {
  if (status === "pass") return "badge badge-ok";
  if (status === "fail") return "badge badge-fail";
  return "badge badge-unmatched";
}

function statusLabel(status: IntercompanyPair["status"]): string {
  if (status === "pass") return "Matches";
  if (status === "fail") return "Does not match";
  return "Missing the other side";
}

function money(value: string | null | undefined): string {
  return moneyText(value, { fallback: "No amount" });
}

function currencyLabel(code: string): string {
  if (code === "USD") return "US dollars";
  return "As stored";
}

function renderPairRow(
  pair: IntercompanyPair,
  dir: CompanyDirectory,
): HTMLTableRowElement {
  const rowClass =
    pair.status === "pass" ? "ic-pair-pass" : `ic-pair-${pair.status}`;
  return h("tr", { class: rowClass, "data-status": pair.status }, [
    h("td", {}, [labelFor(pair.receivableCompany, dir)]),
    h("td", {}, [labelFor(pair.payableCompany, dir)]),
    h("td", { class: "num" }, [money(pair.dueFromBalance)]),
    h("td", { class: "num" }, [money(pair.dueToBalance)]),
    h("td", { class: "num" }, [money(pair.net)]),
    h("td", {}, [
      h("span", { class: statusClass(pair.status) }, [statusLabel(pair.status)]),
      showDetails(pair.status),
    ]),
    h("td", {}, [
      currencyLabel(pair.currency),
      showDetails(pair.currency),
    ]),
  ]);
}

function captionFor(
  snapshot: IntercompanySnapshot,
  dir: CompanyDirectory,
): { face: string; detail: string } {
  if (snapshot.generatedAt === null) {
    return {
      face: "No balances between companies have been saved yet. This page shows the last check, not a live lookup.",
      detail:
        "No intercompany snapshot has been pushed yet. This is the last pipeline check, not a live query.",
    };
  }
  return {
    face: `These amounts are from the last check. ${owedSentence(snapshot, dir)}`,
    detail: `Last pipeline computation ${snapshot.generatedAt}. This is not a live bean-query. Due-From positive = they owe us; Due-To negative = we owe them; net zero is pass. Amounts are shown in dollars.`,
  };
}

/** A balance leg as cents, or null when it is missing or unreadable. */
function legCents(raw: string | null | undefined): bigint | null {
  const text = (raw ?? "").trim();
  if (text === "") return null;
  return decimalToCents(text);
}

/** Which company a signed leg says owes the other, under the pipeline's convention. */
type OwingDirection = "payable" | "receivable" | "unknown" | "none" | "unreadable";

/**
 * Read one pair's two legs.
 *
 * The convention is the pipeline's, and it is written at the top of
 * `pipeline/intercompany.py` (lines 8-11) and glossed in this page's own Show
 * details: **Due-From (an asset) is positive when the counterparty owes us**
 * and **Due-To (a liability) is negative when we owe them**. So the direction
 * follows the *sign*, not the row's orientation: a negative Due-From, or a
 * positive Due-To, says the receivable company owes the payable one. The
 * previous version hard-wired "payable owes receivable" and took the absolute
 * amount, which stated the opposite sentence for exactly those rows.
 *
 * A pair whose legs disagree about the direction, or whose readable legs give
 * different amounts, cannot be summed up in one sentence; neither can a pair
 * with an unreadable leg. Both are reported as such rather than guessed.
 */
function readOwed(pair: IntercompanyPair): {
  direction: OwingDirection;
  cents: bigint | null;
} {
  const from = legCents(pair.dueFromBalance);
  const to = legCents(pair.dueToBalance);
  const votes: OwingDirection[] = [];
  if (from !== null && from !== 0n) votes.push(from > 0n ? "payable" : "receivable");
  if (to !== null && to !== 0n) votes.push(to < 0n ? "payable" : "receivable");
  if (votes.length === 0) {
    // Both legs readable and zero is a real "nothing owed". A missing or
    // unreadable leg is not: the row shows "No amount" and a "Does not match"
    // badge, so the caption must not claim the row is empty.
    const bothReadableZero = from === 0n && to === 0n;
    return { direction: bothReadableZero ? "none" : "unreadable", cents: null };
  }
  const directions = new Set(votes);
  if (directions.size !== 1) return { direction: "unknown", cents: null };
  const direction = votes[0] ?? "unknown";
  // The amount is the owed company's own figure: the receivable's Due-From
  // when the payable company owes it, the payable's Due-To when the receivable
  // owes. On a mismatched pair the two legs disagree on the figure; the claim
  // of the company that is owed is the one this sentence states, and the row
  // below shows both. If that leg is unreadable, the other leg still tells us
  // how much.
  const owedLeg = direction === "payable" ? from : to;
  const otherLeg = direction === "payable" ? to : from;
  for (const leg of [owedLeg, otherLeg]) {
    if (leg !== null && leg !== 0n) return { direction, cents: leg < 0n ? -leg : leg };
  }
  return { direction: "unknown", cents: null };
}

/**
 * The sign lecture ("positive means they owe us") asks the reader to do the
 * arithmetic. One plain sentence says who owes whom, with the amount.
 *
 * The caption never disagrees with the table beside it: it states a direction
 * only when the signs support one, never says "nothing is owed" over a row it
 * could not read, and says how many pairs it did not name rather than implying
 * it listed them all.
 */
function owedSentence(snapshot: IntercompanySnapshot, dir: CompanyDirectory): string {
  const named: Array<{ ower: string; owed: string; amount: string; known: boolean }> = [];
  let anyUnreadable = false;
  let anyUnknownDirection = false;
  for (const pair of snapshot.pairs) {
    const read = readOwed(pair);
    if (read.direction === "none") continue;
    if (read.direction === "unreadable") {
      anyUnreadable = true;
      continue;
    }
    if (read.direction === "unknown" || read.cents === null) {
      anyUnknownDirection = true;
      continue;
    }
    const receivable = companyFace(pair.receivableCompany, dir);
    const payable = companyFace(pair.payableCompany, dir);
    // "payable owes receivable" when the legs are positive-Due-From or
    // negative-Due-To, and the other way round for the reversed signs.
    const paysReceivable = read.direction === "payable";
    named.push({
      ower: paysReceivable ? payable.name : receivable.name,
      owed: paysReceivable ? receivable.name : payable.name,
      amount: formatCents(read.cents),
      known: receivable.known && payable.known,
    });
  }
  if (anyUnreadable) {
    return "Some amounts could not be read. The rows below show what the check found.";
  }
  if (anyUnknownDirection) {
    return "The rows below show what the last check found.";
  }
  if (named.length === 0) {
    return "No company owes another right now.";
  }
  if (!named.every((row) => row.known)) {
    // Someone cannot be named. Say what the check found without naming anyone,
    // so the caption and the table always agree.
    const amounts = named
      .slice(0, 2)
      .map((row) => row.amount)
      .join(" and ");
    return `The check found ${amounts} owed between companies. The rows below say which.`;
  }
  const sentences = named
    .slice(0, 2)
    .map((row) => `${row.ower} owes ${row.owed} ${row.amount}.`);
  const more = named.length - sentences.length;
  if (more > 0) {
    sentences.push(`And ${pluralText(more, "more pair", "more pairs")}.`);
  }
  return sentences.join(" ");
}

/** Fleet view of the last saved balances between companies. Writes stay owner-only. */
export async function renderIntercompanyPage(
  opts: IntercompanyPageOptions,
): Promise<void> {
  const { client, root } = opts;
  // Owner decision 10: the role the shell holds; an unknown role reads as the
  // account manager, never the owner.
  const role = readerRole(opts.role);
  clear(root);
  root.append(h("p", { class: "muted" }, ["Loading balances between companies…"]));

  let snapshot: IntercompanySnapshot;
  const rows: CompanyDirectoryRow[] = [];
  let dir: CompanyDirectory = createCompanyDirectory(rows, { complete: false });
  try {
    const [loaded, companyList] = await Promise.all([
      client.getIntercompany(),
      client.listCompanies().catch(() => null),
    ]);
    snapshot = loaded;
    for (const company of companyList?.companies ?? []) {
      rows.push({ slug: company.slug, displayName: company.displayName });
    }
    if (companyList === null) {
      for (const [slug, label] of opts.companyLabels ?? []) {
        // Only used when the company list failed. The shell supplies display
        // names (rd Y1); a label that is only the slug in another case is not a
        // name and is not shown on the face.
        if (label.toLowerCase() !== slug.toLowerCase()) {
          rows.push({ slug, displayName: label });
        }
      }
    }
    dir = createCompanyDirectory(rows, { complete: companyList !== null });
  } catch (error) {
    if (!(opts.isCurrent?.() ?? true)) return;
    if (
      error instanceof AdminApiError &&
      (error.status === 401 || error.status === 403 || error.status === 503)
    ) {
      throw error;
    }
    clear(root);
    const retry = h(
      "button",
      { type: "button", class: "btn btn-primary", "data-action": "retry" },
      ["Try again"],
    );
    retry.addEventListener("click", () => {
      void renderIntercompanyPage(opts);
    });
    root.append(
      h("header", { class: "page-header" }, [
        h("h1", { class: "page-title", "data-role": "intercompany-title" }, [
          PAGE_TITLE,
        ]),
      ]),
      h("div", { class: "error-state", role: "alert", "data-role": "error-state" }, [
        h("p", {}, ["The balances between companies could not be loaded."]),
        retry,
      ]),
    );
    return;
  }
  if (!(opts.isCurrent?.() ?? true)) return;
  clear(root);

  const freshnessHost = h("div", { "data-role": "freshness-host" });
  root.append(freshnessHost);
  if (snapshot.generatedAt !== null) {
    freshnessHost.append(
      createFreshnessCaption({
        updatedAt: new Date(snapshot.generatedAt),
        onRefresh: () => {
          void renderIntercompanyPage(opts);
        },
      }),
    );
  }

  const caption = captionFor(snapshot, dir);
  root.append(
    h("header", { class: "page-header" }, [
      h("h1", { class: "page-title", "data-role": "intercompany-title" }, [
        PAGE_TITLE,
      ]),
      h("p", { class: "lede" }, [
        "Who owes whom across the companies. Rows that do not match stay visible.",
      ]),
    ]),
  );
  if (role === "account_manager") {
    // rd round 12 decision 2: nothing in this console changes these amounts,
    // for either role; the entry is made in the ledger by hand. The account
    // manager was told "Only the owner can change this.", which sent them
    // looking for an owner-only control that does not exist.
    root.append(
      h("p", { class: "muted", "data-role": "owner-only-note" }, [
        sentence("ledger_by_hand", role),
      ]),
    );
  }
  root.append(
    h("div", { class: "muted", "data-role": "intercompany-caption" }, [
      caption.face,
      showDetails(caption.detail),
    ]),
  );

  if (snapshot.generatedAt === null) {
    root.append(
      h("div", { class: "empty empty-state", "data-role": "intercompany-empty" }, [
        // Nothing in the console runs the check; the pipeline writes the
        // snapshot. Both roles read when it will appear, and neither is sent
        // to the owner for something only the booking run can do.
        "Nothing has been pushed yet. Open this page again after the next check.",
        showDetails(
          "Nothing has been pushed. Open this page again after the pipeline writes the snapshot.",
        ),
      ]),
    );
    return;
  }

  if (snapshot.pairs.length === 0) {
    root.append(
      h("p", { class: "empty empty-state", "data-role": "intercompany-empty-pairs" }, [
        "The last check found no amounts owed between companies. Open this page again after the next check.",
      ]),
    );
  } else {
    const table = h("table", {
      class: "data-table fleet-table",
      "data-role": "intercompany-pairs",
    });
    table.append(
      h("thead", {}, [
        h("tr", {}, [
          h("th", {}, ["Owed to"]),
          h("th", {}, ["Owes"]),
          h("th", { class: "num" }, ["Owed"]),
          h("th", { class: "num" }, ["Owing"]),
          h("th", { class: "num" }, ["Net"]),
          h("th", {}, ["Status"]),
          h("th", {}, ["Currency"]),
        ]),
      ]),
    );
    const tbody = h("tbody", {});
    for (const pair of snapshot.pairs) {
      tbody.append(renderPairRow(pair, dir));
    }
    table.append(tbody);
    root.append(h("div", { class: "table-scroll card" }, [table]));
  }

  const na = snapshot.companies.filter((company) => company.status === "n/a");
  if (na.length > 0) {
    const names = na
      .map((company) => labelFor(company.slug, dir))
      .join(", ");
    root.append(
      h("div", { class: "muted", "data-role": "intercompany-na" }, [
        `These companies do not track money owed between companies: ${names}.`,
        showDetails(`Companies with no intercompany accounts (n/a): ${names}`),
      ]),
    );
  }
}
